import { useState, useEffect } from "react";
import { apiFetch } from "../../../context/AuthContext";
import { getToken, decodeJwtPayload } from "../utils/tokenUtils";

const usePayrollData = ({
  selectedMonth,
  statusFilter,
  selectedDepartment,
  selectedCompany,
  visibilityRules,
  fetchVisibilityRules,
  profile,
  clientName,
  language,
}) => {
  const API_BASE_URL = process.env.REACT_APP_BASE_URL;

  // State for payroll data
  const [payrollData, setPayrollData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [togglingIgnoredIds, setTogglingIgnoredIds] = useState(new Set());
  const [unpayingIds, setUnpayingIds] = useState(new Set());
  // ==================== AUTO-CREATE PAYROLL ON LOAD ====================
  useEffect(() => {
    // Wait until loading is DONE
    if (loading) return;

    const ensurePayrollForAll = async () => {
      const endYear = selectedMonth?.year || new Date().getFullYear();
      const endMonth = selectedMonth?.month || new Date().getMonth() + 1;

      // Use all visible employees, not just those in payrollData
      let codes = [];
      // Try to use visibilityRules if available
      if (Array.isArray(visibilityRules) && visibilityRules.length > 0) {
        codes = visibilityRules
          .map((r) => String(r.employeeInfinityCode || "").trim())
          .filter(Boolean);
      } else {
        // fallback: use payrollData as before
        codes = Array.from(
          new Set(
            payrollData
              .map((p) => String(p.infinityCode || "").trim())
              .filter(Boolean),
          ),
        );
      }

      if (!codes.length) return;

      console.log(
        `🔄 Ensuring payroll exists for ${codes.length} employees (payroll month ${endYear}/${endMonth})...`,
      );

      try {
        const response = await apiFetch(
          `${API_BASE_URL}/payrollX/ensure-exists`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              infinityCodes: codes,
              year: endYear,
              month: endMonth,
            }),
          },
        );

        const result = await response.json();
        const created = result?.data?.created ?? 0;

        if (created > 0) {
          console.log(
            `✅ Auto-created ${created} payroll record(s) — refreshing...`,
          );
          setRefreshTrigger((prev) => prev + 1);
        } else {
          console.log(`✅ All payroll records already exist`);
        }
      } catch (err) {
        console.warn("⚠️ ensurePayrollExists batch call failed:", err.message);
      }
    };

    ensurePayrollForAll();
  }, [loading, payrollData, selectedMonth]); // ✅ add `loading` to deps // Re-run when month changes too
  // Fetch payroll data from API
  useEffect(() => {
    const fetchPayrollData = async () => {
      try {
        setLoading(true);
        setError(null);

        console.log("Starting payroll data fetch...");
        console.log("API Base URL:", API_BASE_URL);

        const params = new URLSearchParams();

        // Ensure selectedMonth is an object with month/year
        if (selectedMonth && typeof selectedMonth === "object") {
          params.append("year", selectedMonth.year || new Date().getFullYear());
          params.append(
            "month",
            selectedMonth.month || new Date().getMonth() + 1,
          );
        } else {
          params.append("year", new Date().getFullYear());
          params.append("month", new Date().getMonth() + 1);
        }

        if (statusFilter && statusFilter !== "All") {
          params.append("paymentStatus", statusFilter.toLowerCase());
        }

        // selectedDepartment is still passed through (used as single employee filter)
        if (
          selectedDepartment &&
          selectedDepartment !== "All" &&
          !isNaN(selectedDepartment)
        ) {
          params.append("infinityCode", selectedDepartment);
        }

        // Resolve current user and role
        let userDataStr =
          localStorage.getItem("user") || sessionStorage.getItem("user");
        let currentUser = null;
        try {
          currentUser = userDataStr ? JSON.parse(userDataStr) : null;
        } catch (e) {
          currentUser = null;
        }
        const currentRole =
          profile?.role ||
          currentUser?.role ||
          localStorage.getItem("role") ||
          sessionStorage.getItem("role");
        let currentAdminInfinityCode =
          currentUser?.infinityCode ||
          currentUser?.employeeInfinityCode ||
          currentUser?.id ||
          null;

        // Try token fallback if needed (robust)
        if (!currentAdminInfinityCode) {
          const token = getToken();
          if (token) {
            try {
              const payload = decodeJwtPayload(token);
              currentAdminInfinityCode =
                payload?.infinityCode ||
                payload?.infinity_code ||
                payload?.id ||
                payload?.employeeInfinityCode ||
                null;
            } catch (e) {
              // ignore
            }
          }
        }

        // Fetch visible employees list for every role except superadmin.
        // Admins were previously exempted here, which let them fetch every
        // employee's payroll (and salary) regardless of visibility rules —
        // that's the bug: admin should be scoped by visibility just like
        // managers, only superadmin sees everything unfiltered.
        let visibleCodes = null; // null means "no filtering"
        if (currentRole && currentRole !== "superadmin") {
          if (currentAdminInfinityCode) {
            try {
              const visResp = await apiFetch(
                `${API_BASE_URL}/payrollX/visibility/admin/${String(
                  currentAdminInfinityCode,
                )}/employees`,
                { method: "GET" },
              );
              if (visResp.ok) {
                const visBody = await visResp.json();
                visibleCodes = (visBody.data || []).map((e) =>
                  String(e.infinityCode),
                );
                console.log(
                  "Visible employee codes for current admin:",
                  visibleCodes,
                );
              } else {
                // fallback to rules endpoint
                const visRulesResp = await apiFetch(
                  `${API_BASE_URL}/payrollX/visibility/rules?adminInfinityCode=${String(
                    currentAdminInfinityCode,
                  )}`,
                  { method: "GET" },
                );
                if (visRulesResp.ok) {
                  const vr = await visRulesResp.json();
                  visibleCodes = (vr.data || []).map((r) =>
                    String(r.employeeInfinityCode),
                  );
                }
              }
            } catch (e) {
              console.warn(
                "Failed to fetch visibility list, proceeding with empty result (no access)",
                e,
              );
              // If visibility fetch fails for a manager, default to empty set to prevent leakage
              visibleCodes = [];
            }
          } else {
            // no admin code -> show nothing
            visibleCodes = [];
          }
        }

        const pageSize = 200;
        params.set("limit", String(pageSize));

        const buildPayrollUrl = (queryParams) => {
          if (clientName != "Infinity") {
            return `${API_BASE_URL}/payrollX/${clientName}/all?${queryParams.toString()}`;
          }
          return `${API_BASE_URL}/payrollX?${queryParams.toString()}`;
        };

        const fetchPayrollPage = async (pageNumber) => {
          const pageParams = new URLSearchParams(params);
          pageParams.set("page", String(pageNumber));

          const pageUrl = buildPayrollUrl(pageParams);
          console.log(`Fetching payroll page ${pageNumber}:`, pageUrl);

          const response = await apiFetch(pageUrl, { method: "GET" });

          if (!response.ok) {
            const errorText = await response.text();
            console.error("Error response:", errorText);
            throw new Error(
              `HTTP error! status: ${response.status} - ${errorText}`,
            );
          }

          return response.json();
        };

        const firstResult = await fetchPayrollPage(1);
        console.log("Received response (page 1):", firstResult);

        let data = Array.isArray(firstResult?.data)
          ? [...firstResult.data]
          : Array.isArray(firstResult)
            ? [...firstResult]
            : [];

        const totalPages = Number(firstResult?.pagination?.totalPages) || 1;

        for (let page = 2; page <= totalPages; page++) {
          const nextResult = await fetchPayrollPage(page);
          const nextData = Array.isArray(nextResult?.data)
            ? nextResult.data
            : Array.isArray(nextResult)
              ? nextResult
              : [];
          data = data.concat(nextData);
        }

        if (!Array.isArray(data)) {
          console.error("Expected array but got:", typeof data, data);
          throw new Error("Invalid response format from server");
        }

        console.log("Number of records before filtering:", data.length);

        // Transform payroll entries
        const transformedData = data.map((payroll) => {
          // Normalize an ISO datetime or date string to "YYYY-MM-DD" for HTML date inputs.
          // Returns "" if the value is falsy or not parseable.
          const toDateString = (val) => {
            if (!val) return "";
            try {
              const d = new Date(val);
              if (isNaN(d.getTime())) return "";
              return d.toISOString().slice(0, 10);
            } catch {
              return "";
            }
          };

          const employee = payroll.employee || {};

          const allowances = payroll.allowances || [];
          const overtimeAllowance = allowances.find(
            (a) => a.type?.toLowerCase() === "overtime",
          );
          const transportationAllowance = allowances.find(
            (a) => a.type?.toLowerCase() === "transportation",
          );
          const mealAllowance = allowances.find(
            (a) => a.type?.toLowerCase() === "meal",
          );
          const mobileAllowance = allowances.find(
            (a) => a.type?.toLowerCase() === "mobile",
          );
          const bonusAllowance = allowances.find(
            (a) => a.type?.toLowerCase() === "bonus",
          );
          const kpiAllowance = allowances.find(
            (a) => a.type?.toLowerCase() === "kpi_adjustment",
          );
          const expensesAllowance = allowances.find(
            (a) => a.type?.toLowerCase() === "expenses",
          );

          const deductions = payroll.deductions || [];
          const penaltyDeduction = deductions.find(
            (d) => d.type?.toLowerCase() === "penalty",
          );
          const advanceDeduction = deductions.find(
            (d) =>
              d.type?.toLowerCase() === "advancement" ||
              d.type?.toLowerCase() === "advance",
          );
          const insuranceDeduction = deductions.find(
            (d) =>
              d.type?.toLowerCase() === "insurance" ||
              d.type?.toLowerCase() === "social_insurance",
          );
          const absenceDeduction = deductions.find(
            (d) => d.type?.toLowerCase() === "absence",
          );
          const sickLeaveDeduction = deductions.find(
            (d) => d.type?.toLowerCase() === "sick_leave_25",
          );
          const delayDeduction = deductions.find(
            (d) => d.type?.toLowerCase() === "delay",
          );

          const allowancesTotal = allowances.reduce(
            (sum, a) => sum + (Number(a.amount) || 0),
            0,
          );
          const deductionsTotal = deductions.reduce(
            (sum, d) => sum + (Number(d.amount) || 0),
            0,
          );

          return {
            id: payroll.id,
            employeeName: (() => {
              const name =
                language === "ar" ? employee.arabicName : employee.sr;

              return typeof name === "string" && name.trim()
                ? name.trim().split(/\s+/).slice(0, 3).join(" ")
                : "N/A";
            })(),

            employeeId: `EMP${String(payroll.infinityCode).padStart(3, "0")}`,
            infinityCode: payroll.infinityCode,
            department:
              employee.department?.name ??
              employee.position ??
              employee.employeeType,
            workingCompany: employee.workingCompany,
            laborMode: employee.laborMode || "",
            englishName: employee.sr || employee.englishName || "",
            arabicName: employee.arabicName || "",
            nationalId: employee.nationalId || "",
            phoneNumber: employee.phoneNumber || "",

            position: employee.position ?? employee.employeeType ?? "N/A",

            basicSalary: Number(payroll.basicSalary) || 0,

            bonus: Number(bonusAllowance?.amount || payroll.bonusValue) || 0,
            bonusAdjustments: Number(kpiAllowance?.amount) || 0,
            kpiScore: Number(payroll.achievement) || 0,

            overtimeHours:
              Number(overtimeAllowance?.hours || payroll.overtimeHours) || 0,
            overtimeTotal:
              Number(overtimeAllowance?.amount || payroll.overtimeValue) || 0,

            transportationAllowance:
              Number(transportationAllowance?.amount || payroll.transportationAllowances) || 0,
            mealAllowance: Number(mealAllowance?.amount) || 0,
            mobileAllowance: Number(mobileAllowance?.amount) || 0,
            expenses:
              Number(expensesAllowance?.amount || payroll.expenses) || 0,

            grossSalary: Number(payroll.grossSalary) || 0,
            netSalary: Number(payroll.netSalary) || 0,

            taxMonth: Number(payroll.monthlyTax) || 0,
            martyrsFund: Number(payroll.martyrsFund) || 0,

            socialInsurance:
              Number(insuranceDeduction?.amount) ||
              Number(employee.insuranceSalary) * 0.11 ||
              Number(payroll.basicSalary) * 0.11 ||
              0,
            penalty:
              Number(penaltyDeduction?.amount || payroll.penalityDaysValue) ||
              0,
            advancePayment: Number(advanceDeduction?.amount) || 0,
            absenceDeduction:
              Number(absenceDeduction?.amount || payroll.absenceDaysValue) || 0,
            sickLeaveDeduction:
              Number(
                sickLeaveDeduction?.amount || payroll.sickLeave25DeductionValue,
              ) || 0,
            delayDeduction: Number(delayDeduction?.amount) || 0,
            totalDeductions: Number(payroll.totalDeductions) || deductionsTotal,
            totalAllowances: Number(payroll.totalAllowances) || allowancesTotal,

            hiringDate: toDateString(employee.hiringDate),
            insuranceSalary: Number(employee.insuranceSalary) || 0,
            // Company share = 18.75% of insurance salary (Egyptian law)
            insuranceCompanyShare: Math.round((Number(employee.insuranceSalary) || 0) * 0.1875),
            // Emergency fund = 1% of insurance salary
            emergencyFund: Math.round((Number(employee.insuranceSalary) || 0) * 0.01),
            markup: Number(payroll.markup) || 0,

            workingDays:
              Math.max(
                0,
                Number(payroll.workingDays || 0) - Number(payroll.absenceDays || 0),
              ) || 0,
            absenceDays: Number(payroll.absenceDays) || 0,
            penalityDays: payroll.penalityDays || 0,
            sickLeave25DeductionDays: payroll.sickLeave25DeductionDays || 0,

            status: payroll.paymentStatus,
            paidDate: payroll.paymentDate,
            paymentMethod: payroll.paymentMethod,

            year: payroll.year,
            month: payroll.month,
            payrollStartDate: payroll.payrollStartDate,
            payrollEndDate: payroll.payrollEndDate,

            personalInfo: {
              email: employee.email || "",
              phone: employee.phoneNumber || "",
              emergencyPhone: employee.emergencyPhoneNumber || "",
              nationalId: employee.nationalId || "",
              dateOfBirth: toDateString(employee.dob),
              gender: employee.gender || "",
              maritalStatus: employee.maritalStatus || "",
              nationality: employee.nationality || "",
              address: employee.address || "",
              city: employee.city || "",
              country: employee.country || "Egypt",
              religion: employee.religion || "",
              age: employee.age || null,
            },

            workInfo: {
              hiringDate: toDateString(employee.hiringDate),
              contractType: employee.laborMode || "Full-time",
              contractCompany: employee.contractCompany || "",
              workingHours: "40 hours/week",
              probationPeriod: employee.periodForBeingRegular || "3 months",
              contractExpiration: toDateString(employee.contractExpiration),
              directManager: employee.firstLineManagerName || "",
              workLocation: employee.location || "",
              employeeType: employee.position ?? employee.employeeType ?? "",
              beRegularDate: employee.beRegularDate || "",
              insuranceStatus: employee.insuranceStatus || "",
              insuranceNumber: employee.insuranceNumber || "",
              insuranceSalary: employee.insuranceSalary || 0,
            },

            bankInfo: {
              bankName: employee.bankName || "",
              accountNumber: employee.accountNumber || "",
              iban: employee.iban || "",
              branchCode: employee.branchCode || "",
            },

            notes: payroll.notes || "",
            isIgnored: payroll.isIgnored || false,
            calculatedBy: payroll.calculatedBy,
            approvedBy: payroll.approvedBy,
            approvedDate: payroll.approvedDate,

            allowances: allowances.map((a) => ({
              ...a,
              amount: Number(a.amount) || 0,
            })),
            deductions: deductions.map((d) => ({
              ...d,
              amount: Number(d.amount) || 0,
            })),
          };
        });

        // If visibleCodes is an array (not null), filter the transformed data
        let finalData = transformedData;
        if (Array.isArray(visibleCodes)) {
          finalData = transformedData.filter((p) =>
            visibleCodes.includes(String(p.infinityCode)),
          );
          console.log(
            "Number of records after visibility filter:",
            finalData.length,
          );
        }

        console.log("Transformed data sample:", finalData[0]);
        setPayrollData(finalData);
      } catch (err) {
        console.error("❌ Error fetching payroll data:", err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchPayrollData();
  }, [
    selectedMonth,
    statusFilter,
    selectedDepartment,
    selectedCompany,
    refreshTrigger,
  ]);

  // Function to refresh data
  const refreshData = () => {
    setRefreshTrigger((prev) => prev + 1);
  };

  const handleToggleIsIgnored = async (payrollId) => {
    setTogglingIgnoredIds((prev) => new Set([...prev, payrollId]));
    try {
      const resp = await apiFetch(
        `${API_BASE_URL}/payrollX/${payrollId}/toggle-ignored`,
        {
          method: "PATCH",
        },
      );
      const data = await resp.json();
      if (!resp.ok) throw new Error(data.error || "Failed to toggle");
      // Refresh full data so recalculated payroll numbers are reflected
      refreshData();
    } catch (err) {
      console.error("Toggle isIgnored failed:", err.message);
    } finally {
      setTogglingIgnoredIds((prev) => {
        const next = new Set(prev);
        next.delete(payrollId);
        return next;
      });
    }
  };

  // Bulk version of handleToggleIsIgnored above — used by the table's
  // selection tool. Doing this as N calls to the single-row endpoint (one
  // HTTP round trip, one DB write, and one full payroll recalculation per
  // selected employee, each followed by its own refreshData()/full refetch)
  // was heavy for anything more than a handful of rows. This instead hits
  // one bulk endpoint that updates every row and reprocesses them grouped by
  // period server-side, then refreshes the table data exactly once here.
  const handleBulkToggleIsIgnored = async (payrollIds, isIgnored) => {
    if (!Array.isArray(payrollIds) || payrollIds.length === 0) return;
    setTogglingIgnoredIds((prev) => {
      const next = new Set(prev);
      payrollIds.forEach((id) => next.add(id));
      return next;
    });
    try {
      const resp = await apiFetch(`${API_BASE_URL}/payrollX/bulk/toggle-ignored`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ payrollIds, isIgnored }),
      });
      const data = await resp.json();
      if (!resp.ok) throw new Error(data.error || "Failed to bulk toggle");
      refreshData();
      return data;
    } finally {
      setTogglingIgnoredIds((prev) => {
        const next = new Set(prev);
        payrollIds.forEach((id) => next.delete(id));
        return next;
      });
    }
  };

  const handleUnpayPayroll = async (payrollId, callbacks = {}) => {
    const { onSuccess, onError } = callbacks;
    setUnpayingIds((prev) => new Set([...prev, payrollId]));
    try {
      const resp = await apiFetch(
        `${API_BASE_URL}/payrollX/${payrollId}/unpay`,
        {
          method: "PATCH",
        },
      );
      const data = await resp.json();
      if (!resp.ok) throw new Error(data.error || "Failed to unpay");
      // Refresh full data so updated payroll status is reflected
      refreshData();
      if (onSuccess) onSuccess(data);
    } catch (err) {
      console.error("Unpay payroll failed:", err.message);
      if (onError) onError(err);
    } finally {
      setUnpayingIds((prev) => {
        const next = new Set(prev);
        next.delete(payrollId);
        return next;
      });
    }
  };

  // Bulk version of handleUnpayPayroll — same motivation as
  // handleBulkToggleIsIgnored above: N calls to the single-row endpoint (N
  // round trips, each followed by its own refreshData()/full refetch) was
  // heavy for a multi-row selection even though unpaying itself is a cheap
  // field reset. One request updates every selected row, then this refreshes
  // the table data exactly once.
  const handleBulkUnpayPayroll = async (payrollIds) => {
    if (!Array.isArray(payrollIds) || payrollIds.length === 0) {
      return { updated: 0, skipped: 0 };
    }
    setUnpayingIds((prev) => {
      const next = new Set(prev);
      payrollIds.forEach((id) => next.add(id));
      return next;
    });
    try {
      const resp = await apiFetch(`${API_BASE_URL}/payrollX/bulk/unpay`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ payrollIds }),
      });
      const data = await resp.json();
      if (!resp.ok) throw new Error(data.error || "Failed to bulk unpay");
      refreshData();
      return data.data;
    } finally {
      setUnpayingIds((prev) => {
        const next = new Set(prev);
        payrollIds.forEach((id) => next.delete(id));
        return next;
      });
    }
  };

  const refreshPayrollData = () => {
    console.log("🔄 Refreshing payroll data...");
    setRefreshTrigger((prev) => prev + 1);
  };

  return {
    payrollData,
    setPayrollData,
    loading,
    error,
    refreshTrigger,
    setRefreshTrigger,
    togglingIgnoredIds,
    unpayingIds,
    refreshData,
    handleToggleIsIgnored,
    handleBulkToggleIsIgnored,
    handleUnpayPayroll,
    handleBulkUnpayPayroll,
    refreshPayrollData,
  };
};

export default usePayrollData;